import * as R from 'ramda';

import { ComplianceTypes } from '@/constants';
import { growthbook } from '@/services';
import {
  ComplianceType,
  ContentOverride,
  CPDCreditInfo,
  CPECreditInfo,
  Episode,
  EpisodeCard,
} from '@/types';
import { camelCaseKeys } from '@/utils';

import { swapCDNHost, swapCDNImageHost } from '../store.utils';

export const transformEpisodeCard = (
  episode: any,
  compliance?: ComplianceType,
): EpisodeCard =>
  R.pipe(
    camelCaseKeys,
    e => ({
      episodeId: e.episodeId,
      name:
        (!compliance
          ? e.name
          : compliance === ComplianceTypes.CPD
            ? e.credits.find((info: CPDCreditInfo) => info.type === 'cpd')?.name
            : e.credits.find((info: CPECreditInfo) => info.type === 'cpe')
                ?.name) ?? e.name,
      hook: e.hook,
      cover: e.cover,
      chapters: e.chapters,
      deepLink: e.deepLink,
      duration: e.duration,
      companyLogo: e.companyLogo,
      companyName: e.companyName,
      isPremium: e.isPremium,
      episodeAgeTag: e.episodeAgeTag,
      isRecordedLive: e.isRecordedLive,
      isVideo: e.isVideo,
      totalEthics: e.totalEthics,
      trailerId: e.trailerId,
      fieldOfStudyId: e.fieldOfStudyId ?? e.facets?.fieldOfStudy?.slug,
      fieldOfStudy: e.fieldOfStudy ?? e.facets?.fieldOfStudy?.value,
      categories: e.categories,
      isArchived: e.isArchived,
      publishDate: e.publishDate,
      audienceRegions: e.audienceRegions,
      facets: e.facets,
      // TODO: Remove legacy credits setup once all endpoints use newer structure
      credits:
        e?.credits?.find?.((info: CPECreditInfo) => info.type === 'cpe')
          ?.credits ?? +e.credits,
      creditSummary:
        typeof episode?.credits === 'object' ? e.credits : undefined,
      ...(!R.isNil(e.totalBoost)
        ? {
            relevance: {
              boostDetails: e.boostDetails,
              totalBoost: e.totalBoost,
            },
          }
        : {}),
    }),
    updateAssetCDN,
    applyEpisodeOverrides,
  )(episode);

// properties such as description significantly bloat the size of the store
// given we fetch hundreds of episode cards
// so we avoid adding them to the base episode card unless necessary
export const transformEpisode = (episode: any): Episode => {
  const baseEpisode = transformEpisodeCard(episode);
  const additionalEpisode = R.pipe(camelCaseKeys, e => ({
    description: (e.formattedDescription ?? e.description)?.replace(
      /\\n/g,
      '  \n',
    ),
    marketingDescription: e.marketingDescription,
    shortDescription: e.shortDescription,
    speakerBio: (e.formattedSpeakerBio ?? e.speakerBio)?.replace(
      /\\n/g,
      '  \n',
    ),
    quote: e.quote,
    callToAction: e.callToAction,
    glossary: e.glossary,
    takeaways: e.takeaways,
    keywords: e.keywords,
    resources: e.resources?.map((r: any) => ({
      ...r,
      file: {
        url: swapCDNHost(r.file?.url),
      },
    })),
    visualAid: {
      ...e.visualAid,
      horizontalPDF: e.visualAid.horizontalPDF && {
        url: swapCDNHost(e.visualAid.horizontalPDF.url),
      },
    },
    shareCover: e.shareCover,
    verifiableChapterCount: e.verifiableChapterCount,
    companyId: e.companyId,
    companyUrl: e.companyUrl,
    isEthics: e.isEthics,
    isTechnical: e.isTechnical,
    assessmentId: e.assessmentId,
    compliance: e.region === 'usa' ? ComplianceTypes.CPE : ComplianceTypes.CPD,
    speakers: e.speakers?.map((s: any) => ({
      ...s,
      companyLogo: swapCDNImageHost(s.companyLogo, true),
    })),
    relatedEpisodes: e.relatedEpisodes,
    CPEDetails: {
      advancedPreparations: e.advancedPreparations,
      fieldOfStudy: e.fieldOfStudy,
      prerequisites: e.prerequisites,
      programLevel: e.programLevel,
      credits: +e.credits,
      publishDate: e.publishDate,
    },
  }))(episode);

  return {
    ...baseEpisode,
    ...additionalEpisode,
  };
};

const applyEpisodeOverrides = (episodeCard: EpisodeCard): EpisodeCard => {
  const result = growthbook
    .getFeatureValue('content-overrides', {
      data: [] as ContentOverride[],
    })
    .data.find(
      (override: ContentOverride) =>
        override.content_type === 'episode' &&
        override.content_id === episodeCard.episodeId,
    );

  return result ? { ...episodeCard, ...result.overrides } : episodeCard;
};

const updateAssetCDN = (episodeCard: EpisodeCard): EpisodeCard => {
  return {
    ...episodeCard,
    cover: swapCDNImageHost(episodeCard.cover, true),
    companyLogo: swapCDNImageHost(episodeCard.companyLogo, true),
  };
};

export const reorderEpisodes = (
  episodeIds: string[],
  episodes: EpisodeCard[],
) => {
  const episodeMap = episodes.reduce(
    (acc, episode) => ({
      ...acc,
      [episode.episodeId]: episode,
    }),
    {} as Record<string, EpisodeCard>,
  );
  return episodeIds.map(episodeId => episodeMap[episodeId]).filter(Boolean);
};
